import React, { useState, useEffect } from 'react';
import { FiMonitor, FiSmartphone, FiTablet, FiGlobe, FiMapPin, FiClock, FiShield, FiActivity, FiX } from 'react-icons/fi';
// import { format, formatDistanceToNow } from 'date-fns'; // Not used
import type { UserSession, SessionSummary, SessionActivity } from '../../api/sessions';
import { sessionAPI as sessionService } from '../../api/sessions';
import { Button } from '../ui/Button/Button';
import { Badge } from '../ui/Badge/Badge';
import { Card } from '../ui/Card/Card';
import Modal from '../Modal/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import styles from './SessionsList.module.css';

import { logger } from '../../utils/logger';

interface SessionsListProps {
  userId?: string;
  organizationId?: string;
  showCurrentSession?: boolean;
  allowRevoke?: boolean;
  showSummary?: boolean;
}

export const SessionsList: React.FC<SessionsListProps> = ({
  userId,
  organizationId,
  // showCurrentSession = true, // Not used
  allowRevoke = true,
  showSummary = true,
}) => {
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [sessionSummary /* , setSessionSummary */] = useState<SessionSummary | null>(null);
  const [selectedSession, setSelectedSession] = useState<UserSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<Set<string>>(new Set());
  const [filterBy, setFilterBy] = useState<'all' | 'active' | 'suspicious'>('all');
  const [sortBy, setSortBy] = useState<'created_at' | 'last_activity' | 'location'>('last_activity');
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [sessionActivity, setSessionActivity] = useState<SessionActivity[]>([]);

  const { /* user */ } = useAuth(); // user not used directly
  const { addToast } = useToast();

  useEffect(() => {
    loadSessions();
    if (showSummary) {
      loadSessionSummary();
    }
  }, [userId, organizationId, filterBy, sortBy]);

  const loadSessions = async () => {
    // Session management endpoints are not implemented in the backend
    // Backend only has 25 endpoints (see API_DOCUMENTATION_INDEX.md)
    // No /auth/sessions or /auth/sessions/summary endpoints exist
    logger.log('⚠️ Session management not implemented in backend');
    setLoading(false);
    setSessions([]);
  };

  const loadSessionSummary = async () => {
    // Session summary endpoint not implemented in backend
    logger.log('⚠️ Session summary not implemented in backend');
  };

  const loadSessionActivity = async (sessionId: string) => {
    try {
      const activity = await sessionService.getSessionActivity(sessionId);
      setSessionActivity(activity);
    } catch (error) {
      logger.error('Failed to load session activity:', error);
      addToast('Failed to load session activity', 'error');
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    if (!allowRevoke) return;

    setRevoking(prev => new Set(prev).add(sessionId));
    try {
      await sessionService.revokeSession(sessionId);
      setSessions(prev => prev.filter(session => session.id !== sessionId));
      setShowRevokeModal(false);
      setSelectedSession(null);
      addToast('Session revoked successfully', 'success');
    } catch (error) {
      logger.error('Failed to revoke session:', error);
      addToast('Failed to revoke session', 'error');
    } finally {
      setRevoking(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  const handleRevokeAllSessions = async () => {
    if (!allowRevoke) return;

    try {
      // TODO: Implement revokeAllSessions when API method is available
      logger.log('Revoke all sessions for user:', userId);
      addToast('Revoke all sessions functionality not yet implemented', 'info');
      setSessions(prev => prev.filter(session => session.is_current));
      addToast('All sessions revoked successfully', 'success');
    } catch (error) {
      logger.error('Failed to revoke all sessions:', error);
      addToast('Failed to revoke all sessions', 'error');
    }
  };

  const openRevokeModal = (session: UserSession) => {
    setSelectedSession(session);
    setShowRevokeModal(true);
  };

  const openActivityModal = async (session: UserSession) => {
    setSelectedSession(session);
    await loadSessionActivity(session.id);
    setShowActivityModal(true);
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType.toLowerCase()) {
      case 'desktop':
      case 'computer':
        return FiMonitor;
      case 'mobile':
      case 'phone':
        return FiSmartphone;
      case 'tablet':
        return FiTablet;
      default:
        return FiMonitor;
    }
  };

  const getSessionStatusColor = (session: UserSession): 'success' | 'warning' | 'error' => {
    if (session.is_current) return 'success';
    if (session.is_active === false) return 'error'; // Use is_active instead of is_suspicious
    if (session.is_active) return 'success';
    return 'warning';
  };

  const getSessionStatusText = (session: UserSession) => {
    if (session.is_current) return 'Current Session';
    if (session.is_active === false) return 'Inactive';
    return session.is_active ? 'Active' : 'Inactive';
  };

  const getLastActivityText = (session: UserSession) => {
    const lastActivity = new Date(session.last_activity);
    const now = new Date();
    const diffInMinutes = (now.getTime() - lastActivity.getTime()) / (1000 * 60);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${Math.floor(diffInMinutes)}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const filterSessions = () => {
    let filtered = [...sessions];

    if (filterBy === 'active') {
      filtered = filtered.filter(session => session.is_active);
    } else if (filterBy === 'suspicious') {
      filtered = filtered.filter(session => session.is_active === false);
    }

    return filtered;
  };

  const filteredSessions = filterSessions();
  const activeSessions = sessions.filter(session => session.is_active);
  // const suspiciousSessions = sessions.filter(session => session.is_active === false); // Not used

  if (loading) {
    return (
      <Card className={styles.sessionsList}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading sessions...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className={styles.sessionsList}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h2 className={styles.title}>
            <FiShield className={styles.titleIcon} />
            Session Management
          </h2>
          <p className={styles.subtitle}>
            Monitor and manage active user sessions
          </p>
        </div>

        <div className={styles.headerActions}>
          <Button
            variant="secondary"
            size="sm"
            onClick={loadSessions}
            disabled={loading}
          >
            <FiActivity className={styles.buttonIcon} />
            Refresh
          </Button>
          
          {allowRevoke && activeSessions.length > 1 && (
            <Button
              variant="error"
              size="sm"
              onClick={handleRevokeAllSessions}
            >
              <FiX className={styles.buttonIcon} />
              Revoke All
            </Button>
          )}
        </div>
      </div>

      {/* Summary */}
      {showSummary && sessionSummary && (
        <div className={styles.summaryGrid}>
          <Card className={styles.summaryCard}>
            <div className={styles.summaryContent}>
              <FiGlobe className={styles.summaryIcon} />
              <div className={styles.summaryText}>
                <div className={styles.summaryValue}>{sessionSummary.total_sessions}</div>
                <div className={styles.summaryLabel}>Total Sessions</div>
              </div>
            </div>
          </Card>
          
          <Card className={styles.summaryCard}>
            <div className={styles.summaryContent}>
              <FiShield className={`${styles.summaryIcon} ${styles.successIcon}`} />
              <div className={styles.summaryText}>
                <div className={styles.summaryValue}>{sessionSummary.active_sessions}</div>
                <div className={styles.summaryLabel}>Active Sessions</div>
              </div>
            </div>
          </Card>
          
          <Card className={styles.summaryCard}>
            <div className={styles.summaryContent}>
              <FiActivity className={`${styles.summaryIcon} ${styles.warningIcon}`} />
              <div className={styles.summaryText}>
                <div className={styles.summaryValue}>{sessionSummary.total_sessions - sessionSummary.active_sessions}</div>
                <div className={styles.summaryLabel}>Suspicious</div>
              </div>
            </div>
          </Card>
          
          <Card className={styles.summaryCard}>
            <div className={styles.summaryContent}>
              <FiMapPin className={styles.summaryIcon} />
              <div className={styles.summaryText}>
                <div className={styles.summaryValue}>{sessionSummary.session_locations.length}</div>
                <div className={styles.summaryLabel}>Locations</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.filters}>
          <FiActivity className={styles.filterIcon} />
          <select
            value={filterBy}
            onChange={(e) => setFilterBy(e.target.value as any)}
            className={styles.filterSelect}
          >
            <option value="all">All Sessions</option>
            <option value="active">Active Only</option>
            <option value="suspicious">Suspicious Only</option>
          </select>
        </div>
        
        <div className={styles.sorting}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className={styles.sortSelect}
          >
            <option value="last_activity">Sort by Last Activity</option>
            <option value="created_at">Sort by Created Date</option>
            <option value="location">Sort by Location</option>
          </select>
        </div>
      </div>

      {/* Sessions Grid */}
      <div className={styles.sessionsGrid}>
        {filteredSessions.length === 0 ? (
          <Card className={styles.emptyState}>
            <FiGlobe className={styles.emptyIcon} />
            <h3 className={styles.emptyTitle}>Session Management Not Available</h3>
            <p className={styles.emptyDescription}>
              Session management endpoints are not implemented in the backend yet.
              <br />
              <br />
              <strong>Available endpoints (25 total):</strong>
              <br />
              • Authentication (5 endpoints)
              <br />
              • User Profile (4 endpoints)
              <br />
              • Organizations (7 endpoints)
              <br />
              • Members (4 endpoints)
              <br />
              • Activities (2 endpoints)
              <br />
              • Phone Verification (3 endpoints)
              <br />
              <br />
              Session management will be added in a future update.
            </p>
          </Card>
        ) : (
          filteredSessions.map((session) => {
            const DeviceIcon = getDeviceIcon(session.device_info?.device_type || 'desktop');
            const isRevoking = revoking.has(session.id);

            return (
              <Card key={session.id} className={styles.sessionCard}>
                <div className={styles.sessionHeader}>
                  <div className={styles.sessionInfo}>
                    <div className={styles.deviceInfo}>
                      <DeviceIcon className={styles.deviceIcon} />
                      <div className={styles.deviceDetails}>
                        <h4 className={styles.deviceName}>{session.device_info?.browser || 'Unknown Browser'}</h4>
                        <p className={styles.browserInfo}>{session.device_info?.os || 'Unknown OS'}</p>
                      </div>
                    </div>
                    
                    <Badge 
                      variant={getSessionStatusColor(session)} 
                      size="sm"
                      className={styles.statusBadge}
                    >
                      {getSessionStatusText(session)}
                    </Badge>
                  </div>
                </div>

                <div className={styles.sessionDetails}>
                  <div className={styles.sessionMeta}>
                    <div className={styles.metaItem}>
                      <FiMapPin className={styles.metaIcon} />
                      <span className={styles.metaText}>{session.location ? `${session.location.city}, ${session.location.country}` : 'Unknown Location'}</span>
                    </div>
                    <div className={styles.metaItem}>
                      <FiClock className={styles.metaIcon} />
                      <span className={styles.metaText}>
                        Last active {getLastActivityText(session)}
                      </span>
                    </div>
                  </div>
                  
                  <div className={styles.sessionStats}>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>IP Address</span>
                      <span className={styles.statValue}>{session.ip_address}</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Started</span>
                      <span className={styles.statValue}>
                        {new Date(session.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {session.is_active === false && (
                    <div className={styles.suspiciousWarning}>
                      <FiActivity className={styles.warningIcon} />
                      <span className={styles.warningText}>
                        This session has been flagged as suspicious
                      </span>
                    </div>
                  )}
                </div>

                <div className={styles.sessionActions}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openActivityModal(session)}
                  >
                    View Activity
                  </Button>
                  
                  {allowRevoke && !session.is_current && (
                    <Button
                      variant="error"
                      size="sm"
                      onClick={() => openRevokeModal(session)}
                      loading={isRevoking}
                      disabled={isRevoking}
                    >
                      <FiX className={styles.buttonIcon} />
                      Revoke
                    </Button>
                  )}
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* Revoke Confirmation Modal */}
      <Modal
        isOpen={showRevokeModal}
        onClose={() => setShowRevokeModal(false)}
        title="Revoke Session"
        size="medium"
      >
        {selectedSession && (
          <div className={styles.modalContent}>
            <div className={styles.modalWarning}>
              <FiActivity className={styles.modalWarningIcon} />
              <div className={styles.modalWarningText}>
                <h4>Are you sure you want to revoke this session?</h4>
                <p>The user will be logged out immediately and will need to sign in again.</p>
              </div>
            </div>
            
            <div className={styles.sessionPreview}>
              <div className={styles.previewItem}>
                <span className={styles.previewLabel}>Device:</span>
                <span className={styles.previewValue}>{selectedSession.device_info?.browser || 'Unknown Browser'}</span>
              </div>
              <div className={styles.previewItem}>
                <span className={styles.previewLabel}>Location:</span>
                <span className={styles.previewValue}>{selectedSession.location ? `${selectedSession.location.city}, ${selectedSession.location.country}` : 'Unknown Location'}</span>
              </div>
              <div className={styles.previewItem}>
                <span className={styles.previewLabel}>IP Address:</span>
                <span className={styles.previewValue}>{selectedSession.ip_address}</span>
              </div>
            </div>

            <div className={styles.modalActions}>
              <Button
                variant="secondary"
                onClick={() => setShowRevokeModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="error"
                onClick={() => selectedSession && handleRevokeSession(selectedSession.id)}
                loading={revoking.has(selectedSession.id)}
              >
                Revoke Session
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Session Activity Modal */}
      <Modal
        isOpen={showActivityModal}
        onClose={() => setShowActivityModal(false)}
        title="Session Activity"
        size="large"
      >
        {selectedSession && (
          <div className={styles.modalContent}>
            <div className={styles.activityHeader}>
              <h4>Activity for {selectedSession.device_info?.browser || 'Unknown Device'}</h4>
              <p>Recent activity and events for this session</p>
            </div>
            
            <div className={styles.activityList}>
              {sessionActivity.map((activity, index) => (
                <div key={index} className={styles.activityItem}>
                  <div className={styles.activityTime}>
                    {new Date(activity.timestamp).toLocaleString()}
                  </div>
                  <div className={styles.activityDetails}>
                    <div className={styles.activityAction}>{activity.activity_details?.action || activity.activity_type}</div>
                    <div className={styles.activityMeta}>
                      {activity.ip_address && (
                        <span>IP: {activity.ip_address}</span>
                      )}
                      {activity.user_agent && (
                        <span>User Agent: {activity.user_agent}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}; 